// ─── Unified 6-Pillar Scoring Engine ─────────────────────────────────────────
// Single source of truth for pillar definitions, scoring, and detail modal content.
// Consumed by PillarGrid (display) and Dashboard (focus areas).

import type { DailyMetrics, WeeklyCheckinEntry } from '../types';
import { calcCircadianMidpoint, calcBaselineMidpoint, calcCircadianZScore, normalizeCircadianZ, interpretCircadianZ } from './circadianPhase';
import { calcSleepVariability, normalizeSleepCV, interpretSleepVariability } from './sleepVariability';
import { calcConfidence } from './confidenceInterval';
import { getDemographicNorms, normalizeDemographic } from './demographicNorms';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface PillarRange {
  label: string;
  min: number;
  max: number;
  color: string;
}

export interface ContributingInput {
  label: string;
  rawValue: string;       // human-readable raw value (e.g. "72", "7h 23m")
  normalized: number;     // 0-100
  weight: number;
}

export interface PillarResult {
  score: number | null;   // 0-100, null if insufficient data
  label: string;          // "Good", "Needs Attention", "Not enough data"
  color: string;
  inputs: ContributingInput[];
  inputCount: number;     // how many inputs contributed
  subMetric: string | null; // one-line sub-metric for the card (e.g. "7h 23m")
  confidence?: number;    // 0-1, data completeness confidence
  confidenceLabel?: string; // "High confidence", "Moderate confidence", etc.
}

export interface PillarDef {
  key: string;
  name: string;
  icon: string;           // tabler icon name
  description: string;
  ranges: PillarRange[];
  minInputs: number;
  maxInputs: number;      // total possible signals for data confidence display
  source: string;
  how: string;
  meaning: string;
}

// ─── Data bundle passed from Dashboard ───────────────────────────────────────

export interface HomeLeavingDay {
  date: string;
  leftHome: boolean;
  awayMins: number;
}

export interface PillarDataBundle {
  yesterday: DailyMetrics | null;
  latestSleep: DailyMetrics | null;  // most recent day with actual sleep data (may differ from yesterday)
  latestHrv: number | null;          // rmssd in ms
  latestCheckin: WeeklyCheckinEntry | null;
  keyLabs: { marker: string; value: number; ref_low: number | null; ref_high: number | null }[];
  weightDelta: number | null;        // lbs over 30d
  symptoms: string[];                // parsed from checkin symptoms string
  restingHR: number | null;
  homeLeaving?: HomeLeavingDay[];    // recent home-leaving data from geofence
  // Demographics (for age/sex-adjusted scoring)
  userAge?: number | null;
  userSex?: 'male' | 'female' | null;
  // Body composition (from Apple Health — smart scale + Apple Watch)
  vo2Max?: number | null;
  vo2MaxDelta?: number | null;
  bmi?: number | null;
  bodyFat?: number | null;
  bodyFatDelta?: number | null;
  // Historical sleep metrics for circadian + variability calculations
  sleepHistory?: DailyMetrics[];
}

// ─── Pillar Definitions ──────────────────────────────────────────────────────

const RANGES_STANDARD: PillarRange[] = [
  { label: 'Needs Attention', min: 0,  max: 39,  color: '#EF4444' },
  { label: 'Fair',            min: 40, max: 59,  color: '#F59E0B' },
  { label: 'Good',            min: 60, max: 79,  color: '#22C55E' },
  { label: 'Excellent',       min: 80, max: 100, color: '#00A896' },
];

export const PILLAR_DEFS: PillarDef[] = [
  {
    key: 'sleep',
    name: 'Sleep',
    icon: 'moon',
    description: 'Recovery and circadian regulation',
    ranges: RANGES_STANDARD,
    minInputs: 1,
    maxInputs: 7,
    source: 'Oura Ring — measured nightly via heart rate, HRV, and movement sensors.',
    how: 'Composite of your Oura sleep score, readiness score, sleep efficiency, total sleep duration, deep sleep time, circadian phase consistency, and sleep variability. Each is weighted and combined — missing inputs are excluded, not penalized.',
    meaning: 'Sleep is the foundation of mental and physical health. Poor sleep impairs cognition, disrupts hormones, and worsens mood. Consistently high scores indicate strong recovery capacity.',
  },
  {
    key: 'movement',
    name: 'Movement',
    icon: 'activity',
    description: 'Physical activity and metabolic activation',
    ranges: RANGES_STANDARD,
    minInputs: 1,
    maxInputs: 2,
    source: 'Apple Watch and Oura Ring — steps, exercise, and VO2 Max.',
    how: 'Steps scored with square-root smoothing toward a 10,000/day target. Exercise minutes scored linearly against 30 min/day. VO2 Max (cardiorespiratory fitness) scored against population norms. VO2 Max trend tracks 30-day trajectory.',
    meaning: 'Regular movement is one of the highest-leverage interventions for both physical and mental health. Even 7,000+ steps/day significantly reduces all-cause mortality risk.',
  },
  {
    key: 'nutrition',
    name: 'Nutrition',
    icon: 'salad',
    description: 'Diet quality, appetite, nutrient status, and metabolic health',
    ranges: RANGES_STANDARD,
    minInputs: 1,
    maxInputs: 13,
    source: 'Check-in self-report, blood work, Apple Health weight and body composition.',
    how: 'Combines diet quality and appetite self-report, key nutrient and metabolic lab markers (Vitamin D, hsCRP, B12, Omega-3, etc.), and body composition (BMI, body fat %, weight trend). When body composition data is available it replaces weight trend alone for a more complete picture.',
    meaning: 'Nutrient deficiencies and metabolic imbalance are strongly linked to depression, fatigue, and cognitive dysfunction. This pillar tracks whether your body has the building blocks it needs.',
  },
  {
    key: 'stress',
    name: 'Stress',
    icon: 'brain',
    description: 'Physiological and psychological stress load',
    ranges: [
      { label: 'High Stress',    min: 0,  max: 39,  color: '#EF4444' },
      { label: 'Moderate',       min: 40, max: 59,  color: '#F59E0B' },
      { label: 'Low Stress',     min: 60, max: 79,  color: '#22C55E' },
      { label: 'Resilient',      min: 80, max: 100, color: '#00A896' },
    ],
    minInputs: 1,
    maxInputs: 5,
    source: 'Oura Ring stress monitoring, HRV, resting heart rate, and check-in self-report.',
    how: 'Combines Oura daytime resilience (time outside high-stress zones, 100 = best), HRV (higher = better autonomic regulation), resting heart rate (lower = calmer baseline), stress self-report, and rumination patterns from check-in. This composite differs from the single Oura resilience value shown in Prior Day — it incorporates multiple signals.',
    meaning: 'Chronic stress accelerates aging, suppresses immunity, and elevates cortisol. This pillar captures both what your body is experiencing (wearable data) and how you perceive your stress (self-report).',
  },
  {
    key: 'social',
    name: 'Social',
    icon: 'users',
    description: 'Belonging, support, and connection',
    ranges: RANGES_STANDARD,
    minInputs: 1,
    maxInputs: 3,
    source: 'Check-in self-report and home-leaving detection (iOS geofence).',
    how: 'Combines your self-reported social connection, social engagement from check-in, and whether you have been leaving home regularly (via passive geofence monitoring). Consecutive days without leaving home lowers the score.',
    meaning: 'Social isolation is as harmful to health as smoking 15 cigarettes a day. Strong social connection is consistently associated with better mental health outcomes and longevity.',
  },
  {
    key: 'substance',
    name: 'Substances',
    icon: 'shield',
    description: 'Impact from substance exposure',
    ranges: [
      { label: 'High Risk',      min: 0,  max: 39,  color: '#EF4444' },
      { label: 'Elevated',       min: 40, max: 59,  color: '#F59E0B' },
      { label: 'Low Risk',       min: 60, max: 79,  color: '#22C55E' },
      { label: 'Minimal',        min: 80, max: 100, color: '#00A896' },
    ],
    minInputs: 1,
    maxInputs: 1,
    source: 'Check-in self-report — substance use patterns.',
    how: 'Based on your self-reported substance use level. Higher scores mean less disruption from substances.',
    meaning: 'Substances like alcohol, nicotine, and cannabis can significantly disrupt sleep architecture, HRV, mood regulation, and recovery. Even moderate use has measurable physiological effects.',
  },
];

// ─── Normalizers ─────────────────────────────────────────────────────────────

const clamp = (v: number) => Math.min(100, Math.max(0, Math.round(v)));

function normalizeCheckin(value: number | null | undefined): number | null {
  if (value == null || value < 0) return null;
  return clamp((value / 3) * 100);
}

function normalizeCheckinInverted(value: number | null | undefined): number | null {
  if (value == null || value < 0) return null;
  return clamp(((3 - value) / 3) * 100);
}

/** Normalize HRV using age/sex-adjusted ranges. Citation: O'Neal 2016 MESA, Geovanini 2020. */
function normalizeHrv(ms: number | null | undefined, age?: number | null, sex?: 'male' | 'female' | null): number | null {
  if (ms == null || ms <= 0) return null;
  const norms = getDemographicNorms({ age, sex }, 'hrv');
  return clamp(normalizeDemographic(ms, norms));
}

function normalizeSleepDuration(mins: number | null | undefined): number | null {
  if (mins == null || mins <= 0) return null;
  const hours = mins / 60;
  // Bands use exclusive lower / inclusive upper so 9h falls in the optimal
  // range (not the secondary one) and the >10h tail descends in two steps
  // instead of a single 35-point cliff at 10.00h.
  if (hours >= 7 && hours <= 9)   return 100;  // optimal
  if (hours >= 6 && hours < 7)    return 70;
  if (hours > 9 && hours <= 10)   return 70;
  if (hours > 10 && hours <= 11)  return 50;   // mild long-sleep
  if (hours >= 5 && hours < 6)    return 40;
  return 20;                                   // <5h or >11h
}

/** Normalize deep sleep using age-adjusted ranges. Citation: Ohayon 2004. */
function normalizeDeepSleep(mins: number | null | undefined, age?: number | null): number | null {
  if (mins == null || mins <= 0) return null;
  const norms = getDemographicNorms({ age }, 'deep_sleep');
  return clamp(normalizeDemographic(mins, norms));
}

/** Normalize resting HR using age/sex-adjusted ranges. Citation: Quer 2020. */
function normalizeRestingHR(bpm: number | null | undefined, age?: number | null, sex?: 'male' | 'female' | null): number | null {
  if (bpm == null || bpm <= 0) return null;
  // For RHR, lower within range is better — invert the score
  const norms = getDemographicNorms({ age, sex }, 'resting_hr');
  if (bpm >= norms.optimal[0] && bpm <= norms.optimal[1]) {
    // Within optimal: 80-100, lower = better
    const range = norms.optimal[1] - norms.optimal[0] || 1;
    return clamp(100 - ((bpm - norms.optimal[0]) / range) * 20);
  }
  if (bpm >= norms.normal[0] && bpm <= norms.normal[1]) {
    return clamp(bpm < norms.optimal[0] ? 90 : 65 - ((bpm - norms.optimal[1]) / (norms.normal[1] - norms.optimal[1])) * 25);
  }
  return bpm > norms.normal[1] ? 20 : 85; // above normal = poor; below normal = excellent (athlete)
}

function normalizeWeightDelta(delta: number | null | undefined): number | null {
  if (delta == null) return null;
  const abs = Math.abs(delta);
  // Stable weight is best. Significant change in either direction is concerning.
  if (abs <= 1) return 100;
  if (abs <= 3) return 75;
  if (abs <= 5) return 50;
  return 25;
}

// Appetite: Normal = ideal, deviations in either direction penalize nutrition
function normalizeAppetite(val: number | null | undefined): number | null {
  if (val == null || val < 0) return null;
  if (val === 2) return 100;  // Normal — ideal
  if (val === 1) return 50;   // Somewhat less
  if (val === 3) return 40;   // Increased — not great for nutrition
  return 25;                   // Much less (0) — worst, significant clinical concern
}

// Lab scoring: within optimal = 100, within ref range but outside optimal = 50, outside ref range = 15
// Canonical marker names — must match normalizeMarker() output in labAliases.ts
const LAB_OPTIMAL: Record<string, { optLow?: number; optHigh?: number }> = {
  'hsCRP':            { optLow: 0,    optHigh: 1.0   },
  'Insulin':          { optLow: 2,    optHigh: 8     },
  'Cortisol':         { optLow: 6,    optHigh: 18    },
  'Vitamin D':        { optLow: 40,   optHigh: 60    },
  'Magnesium':        { optLow: 2.07, optHigh: 2.4   },
  'RBC Magnesium':    { optLow: 5.5,  optHigh: 6.5   },
  'Omega-3 Index':    { optLow: 8,    optHigh: 12    },
  'Ferritin':         { optLow: 50,   optHigh: 100   },
  'Vitamin B12':      { optLow: 500,  optHigh: 1300  },
  'Folate':           { optLow: 15,   optHigh: 9999  },
  'RBC Folate':       { optLow: 500,  optHigh: 9999  },
  'Homocysteine':     { optLow: 0,    optHigh: 7     },
  'TSH':              { optLow: 1.0,  optHigh: 2.5   },
};

function normalizeLab(marker: string, value: number, refLow: number | null, refHigh: number | null): number {
  const opt = LAB_OPTIMAL[marker];
  if (!opt) return 50; // unknown marker — neutral
  const { optLow, optHigh } = opt;
  const inOptimal = (optLow == null || value >= optLow) && (optHigh == null || optHigh >= 9999 || value <= optHigh);
  if (inOptimal) return 100;
  // Check if within standard ref range
  const inRef = (refLow == null || value >= refLow) && (refHigh == null || value <= refHigh);
  if (inRef) return 50;
  return 15;
}

// Rumination score (rum_q1 + rum_q2 + rum_q3, 0-9) → stress signal
function normalizeRumination(q1: number | null, q2: number | null, q3: number | null): number | null {
  if (q1 == null || q2 == null || q3 == null) return null;
  const total = q1 + q2 + q3; // 0-9, higher = worse
  return clamp(((9 - total) / 9) * 100);
}

// ─── Compute All Pillar Scores ───────────────────────────────────────────────

export function computePillarScores(data: PillarDataBundle): Record<string, PillarResult> {
  const { yesterday: d, latestSleep, latestHrv, latestCheckin: c, keyLabs, weightDelta, symptoms, restingHR } = data;
  const results: Record<string, PillarResult> = {};

  // Helper: weighted average from input array
  function composite(inputs: ContributingInput[], minInputs: number, subMetric: string | null, ranges: PillarRange[], maxInputs: number = 5): PillarResult {
    const valid = inputs.filter(i => i.normalized >= 0);
    if (valid.length < minInputs) {
      return { score: null, label: 'Not enough data', color: '#94A3B8', inputs: valid, inputCount: valid.length, subMetric: null, confidence: 0, confidenceLabel: 'Insufficient data' };
    }
    const totalWeight = valid.reduce((s, i) => s + i.weight, 0);
    const score = totalWeight > 0
      ? clamp(valid.reduce((s, i) => s + i.normalized * i.weight, 0) / totalWeight)
      : 0;
    const range = [...ranges].reverse().find(r => score >= r.min);
    const conf = calcConfidence({ dataPointsAvailable: valid.length, dataPointsExpected: maxInputs, newestDataDaysAgo: 0, sourceCount: 1 });
    return {
      score,
      label: range?.label ?? 'Unknown',
      color: range?.color ?? '#94A3B8',
      inputs: valid,
      inputCount: valid.length,
      subMetric,
      confidence: conf.confidence,
      confidenceLabel: conf.label,
    };
  }

  // ── Sleep ──────────────────────────────────────────────────────────────────
  // Use latestSleep as fallback when yesterday has no sleep data.
  // Also check if yesterday has sleep fields (efficiency/duration) even without
  // a computed sleep_score — Oura delays score processing 1-2 days.
  {
    const hasSleepScore = d?.sleep_score != null && d.sleep_score > 0;
    const hasSleepFields = d?.total_sleep_mins != null && d.total_sleep_mins > 0;
    const s = hasSleepScore ? d : hasSleepFields ? d : latestSleep;
    const inputs: ContributingInput[] = [];
    if (s?.sleep_score != null && s.sleep_score > 0)
      inputs.push({ label: 'Sleep Score', rawValue: String(s.sleep_score), normalized: s.sleep_score, weight: 3 });
    if (s?.readiness != null && s.readiness > 0)
      inputs.push({ label: 'Readiness', rawValue: String(s.readiness), normalized: s.readiness, weight: 2 });
    const effN = s?.sleep_efficiency != null ? clamp(s.sleep_efficiency) : null;
    if (effN != null)
      inputs.push({ label: 'Sleep Efficiency', rawValue: `${s?.sleep_efficiency ?? 0}%`, normalized: effN, weight: 1 });
    const durN = normalizeSleepDuration(s?.total_sleep_mins);
    if (durN != null) {
      const totalMins = s?.total_sleep_mins ?? 0;
      const h = Math.floor(totalMins / 60), m = totalMins % 60;
      inputs.push({ label: 'Duration', rawValue: `${h}h ${m}m`, normalized: durN, weight: 1 });
    }
    const deepN = normalizeDeepSleep(s?.deep_sleep_mins, data.userAge);
    if (deepN != null)
      inputs.push({ label: 'Deep Sleep', rawValue: `${s?.deep_sleep_mins ?? 0}m`, normalized: deepN, weight: 1 });

    // Circadian phase Z-score (from 14-day sleep history)
    if (data.sleepHistory && data.sleepHistory.length >= 5 && s?.bedtime_start && s?.total_sleep_mins) {
      const history = data.sleepHistory
        .filter(m => m.bedtime_start && m.total_sleep_mins && m.total_sleep_mins > 0)
        .map(m => ({
          sleepOnset: new Date(m.bedtime_start!),
          wakeTime: new Date(new Date(m.bedtime_start!).getTime() + m.total_sleep_mins! * 60000),
        }));
      // Guard: filter may reduce below usable threshold
      const baseline = history.length >= 3 ? calcBaselineMidpoint(history) : null;
      if (baseline) {
        const todayOnset = new Date(s.bedtime_start);
        const todayWake = new Date(todayOnset.getTime() + s.total_sleep_mins * 60000);
        const todayMid = calcCircadianMidpoint(todayOnset, todayWake);
        const z = calcCircadianZScore(todayMid, baseline.median, baseline.sd);
        const interp = interpretCircadianZ(z);
        const zLabel = `${z >= 0 ? '+' : ''}${z.toFixed(1)} ${interp.label}`;
        inputs.push({ label: 'Circadian Phase', rawValue: zLabel, normalized: normalizeCircadianZ(z), weight: 1 });
      }
    }

    // Sleep variability (efficiency CV over recent history)
    if (data.sleepHistory && data.sleepHistory.length >= 5) {
      const varData = data.sleepHistory
        .filter(m => m.sleep_efficiency != null && m.bedtime_start && m.total_sleep_mins && m.total_sleep_mins > 0)
        .map(m => ({
          efficiency: m.sleep_efficiency!,
          onsetTime: new Date(m.bedtime_start!),
          wakeTime: new Date(new Date(m.bedtime_start!).getTime() + m.total_sleep_mins! * 60000),
          duration: m.total_sleep_mins!,
        }));
      // Guard: filter may reduce below usable threshold
      const variability = varData.length >= 3 ? calcSleepVariability(varData, 14) : null;
      if (variability) {
        const interp = interpretSleepVariability(variability.efficiencyCV);
        inputs.push({
          label: 'Sleep Consistency',
          rawValue: `${variability.efficiencyCV}% CV — ${interp.label}`,
          normalized: normalizeSleepCV(variability.efficiencyCV),
          weight: 1,
        });
      }
    }

    const subMetric = s?.total_sleep_mins
      ? `${Math.floor(s.total_sleep_mins / 60)}h ${s.total_sleep_mins % 60}m`
      : null;
    results.sleep = composite(inputs, 1, subMetric, PILLAR_DEFS[0].ranges, PILLAR_DEFS[0].maxInputs);
  }

  // ── Movement ───────────────────────────────────────────────────────────────
  {
    const inputs: ContributingInput[] = [];

    // Steps — scored with sqrt smoothing, target 10,000/day
    if (d?.steps != null) {
      const stepsN = clamp(Math.sqrt(Math.min(1, d.steps / 10000)) * 100);
      const stepsStr = d.steps >= 1000
        ? `${Math.round(d.steps).toLocaleString()} steps`
        : `${d.steps} steps`;
      inputs.push({ label: 'Steps', rawValue: stepsStr, normalized: stepsN, weight: 1 });
    }

    // Exercise — linear, target 30 min/day
    if (d?.exercise_mins != null) {
      const exN = clamp(Math.min(1, d.exercise_mins / 30) * 100);
      inputs.push({ label: 'Exercise', rawValue: `${Math.round(d.exercise_mins)} min`, normalized: exN, weight: 1 });
    }

    // VO2 Max — cardiorespiratory fitness level (AHA/ACSM population norms)
    if (data.vo2Max != null) {
      const vo2 = data.vo2Max;
      const vo2N = vo2 >= 45 ? 100 : vo2 >= 38 ? 75 : vo2 >= 30 ? 50 : vo2 >= 25 ? 25 : 10;
      inputs.push({ label: 'VO2 Max', rawValue: `${vo2} mL/kg/min`, normalized: vo2N, weight: 2 });
    }

    // VO2 Max trend — 30-day trajectory (positive = improving)
    if (data.vo2MaxDelta != null) {
      const trendN = clamp(50 + data.vo2MaxDelta * 10);
      const sign = data.vo2MaxDelta > 0 ? '+' : '';
      inputs.push({ label: 'VO2 Max Trend', rawValue: `${sign}${data.vo2MaxDelta.toFixed(1)}`, normalized: trendN, weight: 1 });
    }

    const subMetric = d?.steps != null
      ? (d.steps >= 1000 ? `${(d.steps / 1000).toFixed(1)}k steps` : `${d.steps} steps`)
      : null;
    results.movement = composite(inputs, 1, subMetric, PILLAR_DEFS[1].ranges, PILLAR_DEFS[1].maxInputs);
  }

  // ── Nutrition / Metabolic ──────────────────────────────────────────────────
  {
    const inputs: ContributingInput[] = [];
    const dietN = normalizeCheckin(c?.diet);
    if (dietN != null) {
      const labels = ['Poor', 'Fair', 'Good', 'Excellent'];
      inputs.push({ label: 'Diet Quality', rawValue: labels[c?.diet ?? 0], normalized: dietN, weight: 3 });
    }
    // Lab markers
    // Canonical marker names — must match normalizeMarker() output in labAliases.ts
    const nutritionMarkers = ['Vitamin D', 'Magnesium', 'RBC Magnesium', 'hsCRP',
      'Insulin', 'Vitamin B12', 'Folate', 'RBC Folate',
      'Omega-3 Index', 'Ferritin', 'Homocysteine'];
    const seenMarkerBase = new Set<string>();
    for (const lab of keyLabs) {
      if (!nutritionMarkers.includes(lab.marker)) continue;
      // Deduplicate alternate names (e.g. "Vitamin D" and "25-OH Vitamin D")
      const base = lab.marker.replace(/^25-OH |^RBC |^Fasting |^Vitamin /, '');
      if (seenMarkerBase.has(base)) continue;
      seenMarkerBase.add(base);
      const n = normalizeLab(lab.marker, lab.value, lab.ref_low, lab.ref_high);
      inputs.push({ label: lab.marker, rawValue: String(lab.value), normalized: n, weight: 1 });
    }
    // Body Composition composite (or weight delta fallback)
    {
      const subScores: number[] = [];
      const parts: string[] = [];
      // BMI classification
      if (data.bmi != null) {
        const bmiN = data.bmi < 18.5 ? 40 : data.bmi < 25 ? 100 : data.bmi < 30 ? 60 : 30;
        subScores.push(bmiN);
        parts.push(`BMI ${data.bmi}`);
      }
      // Body fat classification (sex-neutral thresholds)
      if (data.bodyFat != null) {
        const bfN = data.bodyFat < 28 ? 100 : data.bodyFat < 35 ? 60 : 30;
        subScores.push(bfN);
        parts.push(`BF ${data.bodyFat}%`);
      }
      // Weight trend (reuse existing normalizer)
      const wdN = normalizeWeightDelta(weightDelta);
      if (wdN != null) subScores.push(wdN);

      if (subScores.length > 0 && (data.bmi != null || data.bodyFat != null)) {
        // Have body composition data — use composite
        const compScore = Math.round(subScores.reduce((a, b) => a + b, 0) / subScores.length);
        inputs.push({ label: 'Body Composition', rawValue: parts.join(' · '), normalized: compScore, weight: 2 });
      } else if (wdN != null) {
        // Fallback: weight delta alone
        const sign = (weightDelta ?? 0) > 0 ? '+' : '';
        inputs.push({ label: 'Weight Trend (30d)', rawValue: `${sign}${weightDelta ?? 0} lb`, normalized: wdN, weight: 1.5 });
      }
    }
    // Appetite — structured field (new), fallback to symptom string (old check-ins)
    const appetiteLabels = ['Much less', 'Somewhat less', 'Normal', 'Increased'];
    const appetiteVal = c?.appetite;
    const appN = normalizeAppetite(appetiteVal);
    if (appN != null) {
      inputs.push({ label: 'Appetite', rawValue: appetiteLabels[appetiteVal ?? 0], normalized: appN, weight: 2 });
    } else {
      // Backwards compat: old check-ins stored appetite as symptom checkbox
      const hasAppetiteSymptom = symptoms.some(s => s === 'Increased appetite' || s === 'Decreased appetite');
      if (hasAppetiteSymptom) {
        const which = symptoms.find(s => s === 'Increased appetite' || s === 'Decreased appetite')!;
        const legacyN = which === 'Increased appetite' ? 40 : 25;
        inputs.push({ label: 'Appetite', rawValue: which, normalized: legacyN, weight: 2 });
      }
    }

    const labCount = inputs.filter(i => nutritionMarkers.includes(i.label)).length;
    const subMetric = labCount > 0 ? `${labCount} lab marker${labCount > 1 ? 's' : ''}` : (dietN != null ? 'self-report' : null);
    results.nutrition = composite(inputs, 1, subMetric, PILLAR_DEFS[2].ranges, PILLAR_DEFS[2].maxInputs);
  }

  // ── Stress Regulation ──────────────────────────────────────────────────────
  {
    const inputs: ContributingInput[] = [];
    if (d?.stress_score != null)
      inputs.push({ label: 'Stress Resilience', rawValue: `${d.stress_score}/100`, normalized: d.stress_score, weight: 3 });
    const hrvN = normalizeHrv(latestHrv, data.userAge, data.userSex);
    if (hrvN != null)
      inputs.push({ label: 'HRV', rawValue: `${latestHrv}ms`, normalized: hrvN, weight: 2 });
    const rhrN = normalizeRestingHR(restingHR ?? d?.lowest_hr, data.userAge, data.userSex);
    if (rhrN != null)
      inputs.push({ label: 'Resting HR', rawValue: `${restingHR ?? d?.lowest_hr} bpm`, normalized: rhrN, weight: 1 });
    // Stress check-in (0 = Overwhelmed → 3 = Thriving, higher = better)
    const stressN = normalizeCheckin(c?.stress);
    if (stressN != null) {
      const labels = ['Overwhelmed', 'Struggling', 'Managing', 'Thriving'];
      inputs.push({ label: 'Stress Self-Report', rawValue: labels[c?.stress ?? 0], normalized: stressN, weight: 2 });
    }
    // Rumination
    const rumN = normalizeRumination(c?.rum_q1 ?? null, c?.rum_q2 ?? null, c?.rum_q3 ?? null);
    if (rumN != null)
      inputs.push({ label: 'Rumination', rawValue: `${(c?.rum_q1 ?? 0) + (c?.rum_q2 ?? 0) + (c?.rum_q3 ?? 0)}/9`, normalized: rumN, weight: 1 });

    results.stress = composite(inputs, 1, null, PILLAR_DEFS[3].ranges, PILLAR_DEFS[3].maxInputs);
  }

  // ── Social Connection ──────────────────────────────────────────────────────
  {
    const inputs: ContributingInput[] = [];
    const socialN = normalizeCheckin(c?.social);
    if (socialN != null) {
      const labels = ['Isolated', 'Disconnected', 'Connected', 'Thriving'];
      inputs.push({ label: 'Social Connection', rawValue: labels[c?.social ?? 0], normalized: socialN, weight: 3 });
    }
    // BADS Q3: social engagement (0=Not at all → 3=Actively)
    const badsN = normalizeCheckin(c?.bads_q3);
    if (badsN != null)
      inputs.push({ label: 'Social Engagement', rawValue: ['Not at all', 'A little', 'Somewhat', 'Actively'][c?.bads_q3 ?? 0], normalized: badsN, weight: 1 });

    // Home-leaving (geofence) — consecutive home-bound days reduce Social score
    if (data.homeLeaving && data.homeLeaving.length >= 3) {
      const sorted = [...data.homeLeaving].sort((a, b) => b.date.localeCompare(a.date)); // most recent first
      let streak = 0;
      for (const day of sorted) {
        if (!day.leftHome) streak++;
        else break;
      }
      // Score: 0 home-bound = 100, 1 = 80, 2 = 60, 3 = 40, 4 = 20, 5+ = 0
      const homeBoundScore = clamp(Math.max(0, 100 - streak * 20));
      const label = streak === 0 ? 'Active' : streak <= 2 ? `${streak}d home` : `${streak}d home-bound`;
      inputs.push({ label: 'Home Leaving', rawValue: label, normalized: homeBoundScore, weight: 2 });
    }

    results.social = composite(inputs, 1, null, PILLAR_DEFS[4].ranges, PILLAR_DEFS[4].maxInputs);
  }

  // ── Substance Exposure ─────────────────────────────────────────────────────
  {
    const inputs: ContributingInput[] = [];
    const subN = normalizeCheckin(c?.substances);
    if (subN != null) {
      const labels = ['Problematic', 'Elevated', 'Moderate', 'None/Min'];
      inputs.push({ label: 'Substance Use', rawValue: labels[c?.substances ?? 0], normalized: subN, weight: 3 });
    }

    results.substance = composite(inputs, 1, null, PILLAR_DEFS[5].ranges, PILLAR_DEFS[5].maxInputs);
  }

  return results;
}

// ─── Helper: get range label + color for a score ─────────────────────────────

export function pillarColor(score: number | null | undefined): string {
  if (score == null || score === 0) return '#94A3B8';
  if (score >= 80) return '#00A896';
  if (score >= 60) return '#22C55E';
  if (score >= 40) return '#F59E0B';
  return '#EF4444';
}
